import { Box, Button, Typography } from "@mui/material";
import GoBack from "Components/GoBack";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import styles from "./CancellationOffer.module.scss";
import useWindowSize from "Hooks/useWindowSize";
import useQueryParams from "Hooks/useQueryParams";
import { useGetSubscriptionType } from "./CancellationOffer.hooks";
import { AvironLoading } from "Components/Icons/AvironLoading";
import { useHistory } from "react-router-dom";
import { CONSTANTS } from "consts";
import Swal from "sweetalert2";
import { MembershipType, useTrackingData } from "Components/TrackingData";
import { checkIsUpgradeSubscription } from "utils/handleCheckUpgradeSubscription";
import { useCheckIsABTest } from "Hooks/useCheckIsABTest";
import posthog from "posthog-js";
import isInteger  from "lodash/isInteger";
import { handleCheckIsAnnualMembership, handleCheckIsMonthlyMembership, handleCheckIsUpgradeMembership, handleCheckIsUSDCurrency, handleCheckIsWarrantyMembership } from "Screens/Memberships/utils/Membership.utils";


enum MEMBERSHIP_INTERVAL {
    MONTHLY = "monthly",
    ANNUAL = "yearly",
}

interface OfferItem {
    id: number;
    title: string;
    originalPrice: number;
    discountedPrice: number;
    type: string;
    currency: string;
    intervalCount: number
}



const CancellationOffer = () => {
    const [active, setActive] = useState<OfferItem | undefined>(undefined);
    const isDesktop = useWindowSize(1280).width > 768;
    const history = useHistory();
    const { id = "" } = useQueryParams();
    const { subscriptionData, isLoadingSubscription, error } =
        useGetSubscriptionType(id);
    const {
        trackingOfferPageView,
        trackingOfferRedeemClick,
        trackingOfferRejectClick,
    } = useTrackingData(id);
    const isABTestCancelFlowAnnual = useCheckIsABTest("cancellation-flow-annual", true)
    const handleRenderErrorContent = () => {
        return (
            <>
                {Swal.fire("Error", "Something went wrong", "error").then(
                    (result) => {
                        if (result.isConfirmed) {
                            history.replace(
                                CONSTANTS.NAVIGATION_URL.MEMBERSHIPS
                            );
                        }
                    }
                )}
                ;
            </>
        );
    };

    const OFFER_OPTIONS = useMemo(() => {
        const isUSDCurrency = handleCheckIsUSDCurrency(subscriptionData?.avironSubscription?.plan?.currency || "");
        const isAnnualMembership = handleCheckIsAnnualMembership(subscriptionData?.avironSubscription?.plan?.interval || "");
        const baseOffers = [
            {
                id: 1,
                title: "Get 2 Months At 50% Off",
                originalPrice: isUSDCurrency ? 34 : 44,
                discountedPrice: 0.5,
                type: "monthly",
                currency: "usd",
                intervalCount: 2,
            },
            {
                id: 2,
                title: "Get 1 Year At 25% Off",
                originalPrice: isUSDCurrency ? 348 : 456,
                discountedPrice: 0.75,
                type: "yearly",
                currency: "usd",
                intervalCount: 1,
            },
        ];
        setActive(baseOffers[1]);
        return isAnnualMembership ? [baseOffers[1]] : baseOffers
    }, [subscriptionData])


    const isWarrantySubscription = useMemo(() => {
        return handleCheckIsWarrantyMembership(subscriptionData?.avironSubscription?.plan?.productId || "")
    },[subscriptionData]);

    useEffect(() => {
        if (subscriptionData?.avironSubscription) {
          const isAnnualMembership = handleCheckIsAnnualMembership(subscriptionData?.avironSubscription?.plan?.interval as string)
          if(isAnnualMembership) {
            posthog.capture("web_annual_cancel_view_offer")
          }
          trackingOfferPageView(subscriptionData);
        }
    }, [subscriptionData]);

    const isUpgradeSubscripion = useMemo(() => {
        return handleCheckIsUpgradeMembership(subscriptionData?.avironSubscription?.plan?.maxMachines || 1)
    }, [subscriptionData]);

    const isMonthlyMembership = useMemo(
        () =>
            handleCheckIsMonthlyMembership(subscriptionData?.avironSubscription?.plan?.interval || ""),
        [subscriptionData]
    );

    const isAnnualMembership = useMemo(
        () => handleCheckIsAnnualMembership(subscriptionData?.avironSubscription?.plan?.interval || ""),
        [subscriptionData]
    );

    if (isLoadingSubscription) return <AvironLoading />;

    if (Boolean(error) || isUpgradeSubscripion) {
        handleRenderErrorContent();
    }

    return (
        <div className={styles["cancellation-offer"]}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Membership Cancellation Offer | Aviron</title>
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <GoBack />
            {isABTestCancelFlowAnnual ? (
              <Box
                  sx={{
                      padding: {
                          xs: "12px 16px",
                          sm: "40px",
                          backgroundColor: "#fff",
                          marginTop: "24px",
                      },
                  }}
              >
                  <Typography
                      sx={{
                          fontWeight: 700,
                          fontSize: {
                              xs: "20px",
                              sm: "32px",
                          },
                          letterSpacing: "0.1px",
                          textAlign: "center",
                          marginBottom: "8px",
                          fontFamily: "GRIFTER",
                          color: "#000",
                      }}
                  >
                      STAY AND SAVE
                  </Typography>
                  <Typography
                      sx={{
                          fontWeight: 400,
                          fontSize: "16px",
                          lineHeight: "20px",
                          color: "#000",
                          textAlign: "center",
                      }}
                  >
                      Don’t miss out on the fun and keep accessing {isDesktop && <br />} your
                      favourite workouts.
                  </Typography>
                  <Box
                      sx={{
                          marginTop: {
                              xs: "32px",
                              md: "40px",
                          },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: {
                              xs: "column",
                              md: "row",
                          },
                      }}
                  >
                      {OFFER_OPTIONS.map((offerItem, index) => {
                          const isFirstItem = index === 0;
                          return (
                              <Box
                                  key={offerItem.id}
                                  sx={{
                                      display: "flex",
                                      flexDirection: {
                                          xs: "row",
                                          md: "column",
                                      },
                                      marginRight: {
                                          xs: 0,
                                          md: isFirstItem ? "16px" : "0",
                                      },
                                      marginBottom: {
                                          xs: !isFirstItem ? "16px" : "0",
                                          md: "0px",
                                      },
                                      padding: {
                                          xs: "24px 16px 16px 10px",
                                          md: "16px 16px 24px 16px",
                                      },
                                      border:
                                          active?.id === offerItem.id
                                              ? "2px solid #FF4106"
                                              : "1px solid #C3C3C3",
                                      backgroundColor: "#fff",
                                      borderRadius: "12px",
                                      width: {
                                          xs: "fill-available",
                                          md: "306px",
                                      },
                                      cursor: "pointer",
                                      position: "relative",
                                      WebkitTapHighlightColor: "transparent",
                                  }}
                                  order={isFirstItem && !isDesktop ? 2 : 1}
                                  onClick={() => setActive(offerItem)}
                              >
                                  <Box
                                      sx={{
                                          width: {
                                              xs: "50px",
                                              md: "54px",
                                          },
                                          height: {
                                              xs: "44px",
                                              md: "48px",
                                          },
                                          marginBottom: {
                                              xs: "0",
                                              md: "24px",
                                          },
                                          marginRight: {
                                              xs: "5px",
                                              md: "0",
                                          },
                                      }}
                                  >
                                      <img
                                          src="/images/membership/tag-discount.png"
                                          alt="Avriron Offer"
                                          style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "contain",
                                          }}
                                      />
                                  </Box>
                                  <Box>
                                      <Typography
                                          sx={{
                                              fontWeight: 700,
                                              fontSize: {
                                                  xs: "18px",
                                                  md: "20px",
                                              },
                                              color: "#161616",
                                              lineHeight: "20px",
                                              marginBottom: {
                                                  xs: "8px",
                                                  md: "12px",
                                              },
                                              fontFamily: "GRIFTER",
                                              textTransform: "uppercase",
                                          }}
                                      >
                                          {offerItem.title}
                                      </Typography>
                                      <Box
                                          sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              marginBottom: "24px",
                                          }}
                                      >
                                          <Typography
                                              sx={{
                                                  fontFamily: "MonoSpec",
                                                  fontWeight: 500,
                                                  fontSize: "12px",
                                                  lineHeight: "20px",
                                                  color: "#E70808",

                                                  marginRight: "10px",
                                                  letterSpacing: "1.1px",
                                              }}
                                          >
                                              $
                                              <span
                                                  style={{
                                                      textDecoration:
                                                          "line-through",
                                                  }}
                                              >
                                                  {Number(
                                                      offerItem.originalPrice
                                                  )}
                                              </span>
                                          </Typography>
                                          <Typography
                                              sx={{
                                                  fontFamily: "MonoSpec",
                                                  fontWeight: 500,
                                                  fontSize: "16px",
                                                  lineHeight: "16px",
                                                  color: "#000",
                                                  letterSpacing: "1.2px",
                                                  display: "flex",
                                                  alignItems: "flex-start",
                                              }}
                                          >
                                              <span
                                                  style={{ fontSize: "12px" }}
                                              >
                                                  $
                                              </span>
                                              {isInteger(
                                                  offerItem.originalPrice *
                                                      offerItem.discountedPrice
                                              )
                                                  ? Number(
                                                      offerItem.originalPrice *
                                                          offerItem.discountedPrice
                                                  )
                                                  : Number(
                                                      offerItem.originalPrice *
                                                          offerItem.discountedPrice
                                                  ).toFixed(2)}
                                              /
                                              {offerItem.type === "monthly"
                                                  ? "MO"
                                                  : "YEAR"}
                                          </Typography>
                                      </Box>
                                      <Typography
                                          sx={{
                                              backgroundColor: "#EF0707",
                                              color: "#fff",
                                              borderRadius: "100px",
                                              display: "inline-block",
                                              padding: "4px 8px",
                                              fontSize: "12px",
                                              fontWeight: 600,
                                              lineHeight: "16px",
                                          }}
                                      >
                                          $
                                          {(Number(offerItem.originalPrice) -
                                              offerItem.originalPrice *
                                                  offerItem.discountedPrice) *
                                              offerItem.intervalCount}{" "}
                                          IN SAVINGS TODAY
                                      </Typography>
                                  </Box>
                                  {!isFirstItem && (
                                      <Typography
                                          sx={{
                                              backgroundColor: "#FFC300",
                                              color: "#000",
                                              borderRadius: "100px",
                                              display: "inline-block",
                                              padding: "4px 8px",
                                              position: "absolute",
                                              top: "-16px",
                                              left: "16px",
                                              fontSize: "10px",
                                              fontWeight: 600,
                                              lineHeight: "16px",
                                              border: "1px solid #000",
                                          }}
                                      >
                                          BEST VALUE
                                      </Typography>
                                  )}
                              </Box>
                          );
                      })}
                  </Box>
                  <Box
                      sx={{
                          textAlign: "center",
                          marginTop: "40px",
                      }}
                  >
                      <Button
                          variant="contained"
                          color="secondary"
                          sx={{
                              marginLeft: {
                                  xs: "0",
                                  md: "16px",
                              },
                              fontWeight: 500,
                              fontSize: "16px",
                              lineHeight: "19.98px",
                              letterSpacing: "0.01em",
                              width: {
                                  xs: "100%",
                                  md: "342px",
                              },
                              marginBottom: "32px",
                          }}
                          onClick={() => {
                              trackingOfferRedeemClick(
                                  subscriptionData,
                                  active?.type === "monthly" ? "monthly" : "annual" as MembershipType
                              );
                              history.push(
                                  `${CONSTANTS.NAVIGATION_URL.MEMBERSHIP_REDEEM_OFFER}?id=${id}&typeChoosing=${active?.type}&isWarranty=${isWarrantySubscription}`
                              );
                          }}
                      >
                          REDEEM OFFER
                      </Button>
                      <Typography
                          sx={{
                              fontWeight: 400,
                              fontSize: "16px",
                              lineHeight: "20px",
                              color: "#4F4F4F",
                              textDecoration: "underline",
                              cursor: "pointer",
                              paddingBottom: {
                                  xs: "16px",
                                  md: "0px",
                              },
                          }}
                          onClick={() => {
                              trackingOfferRejectClick(
                                  subscriptionData,
                                  active?.type === "monthly" ? "monthly" : "annual" as MembershipType
                              );
                              history.push(
                                  `${CONSTANTS.NAVIGATION_URL.MEMBERSHIP_REVIEW_AND_CANCEL}?id=${id}&entry=offer_reject_click`
                              );
                          }}
                      >
                          No thanks, I still want to cancel
                      </Typography>
                  </Box>
              </Box>
            ) : (
                <Box
                    sx={{
                        padding: {
                            xs: "12px 16px",
                            sm: "40px",
                            backgroundColor: "#fff",
                            marginTop: "24px",
                        },
                    }}
                >
                    <div className={styles["hand-gift"]}>
                        <img
                            src="/images/membership/hand-gift.png"
                            alt="gift"
                        />
                    </div>
                    <Typography
                        sx={{
                            fontSize: "16px",
                            lineHeight: "20px",
                            textAlign: "center",
                            fontWeight: 500,
                            marginBottom: {
                                xs: "24px",
                                md: "16px",
                            },
                        }}
                    >
                        Thank you for being a valued Aviron member!
                    </Typography>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "32px",
                            lineHeight: "35.44px",
                            fontFamily: "GRIFTER",
                            letterSpacing: "0.05em",
                            textAlign: "center",
                            marginBottom: "16px",
                        }}
                    >
                        {isMonthlyMembership && (
                            <>
                                <span className={styles["high-light-text"]}>
                                    TODAY ONLY,
                                </span>
                                GET 50% OFF YOUR MONTHLY {isDesktop && <br />}{" "}
                                MEMBERSHIP FOR 2 MONTHS
                            </>
                        )}
                        {isAnnualMembership && (
                            <>
                                <span className={styles["high-light-text"]}>
                                    TODAY ONLY,
                                </span>
                                GET 25% OFF YOUR ANNUAL
                                {isDesktop && <br />} MEMBERSHIP FOR A YEAR
                            </>
                        )}
                    </Typography>
                    <Typography
                        sx={{
                            fontWeight: 400,
                            fontSize: "16px",
                            lineHeight: "20px",
                            textAlign: "center",
                            color: "#000",
                            marginBottom: {
                                xs: "32px",
                                md: "40px",
                            },
                        }}
                    >
                        Don't miss out on the fun and keep accessing{" "}
                        {isDesktop && <br />} your favorite workouts.
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: {
                                xs: "column",
                                md: "row",
                            },
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                trackingOfferRejectClick(subscriptionData);
                                history.push(
                                    `${CONSTANTS.NAVIGATION_URL.MEMBERSHIP_REVIEW_AND_CANCEL}?id=${id}&entry=offer_reject_click`
                                );
                            }}
                            sx={{
                                marginBottom: {
                                    xs: "16px",
                                    md: 0,
                                },
                                fontWeight: 500,
                                fontSize: "16px",
                                lineHeight: "19.98px",
                                letterSpacing: "0.01em",
                            }}
                        >
                            NO, THANKS
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            sx={{
                                marginLeft: {
                                    xs: "0",
                                    md: "16px",
                                },
                                fontWeight: 500,
                                fontSize: "16px",
                                lineHeight: "19.98px",
                                letterSpacing: "0.01em",
                            }}
                            onClick={() => {
                                trackingOfferRedeemClick(subscriptionData);
                                history.push(
                                    `${CONSTANTS.NAVIGATION_URL.MEMBERSHIP_REDEEM_OFFER}?id=${id}&isWarranty=${isWarrantySubscription}`
                                );
                            }}
                        >
                            REDEEM OFFER
                        </Button>
                    </Box>
                </Box>
            )}
        </div>
    );
};

export default CancellationOffer;
